import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, CalendarPlus, Phone, MapPin, Video } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError, API } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { BookCheckin, slotLabel } from "@/components/messages/BookCheckin";

const ICON = { phone: Phone, in_person: MapPin, video: Video };

const gDate = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const googleUrl = (c) => `https://calendar.google.com/calendar/render?${new URLSearchParams({
  action: "TEMPLATE", text: c.cal_title, dates: `${gDate(c.start)}/${gDate(c.end)}`, details: c.cal_details, ...(c.cal_location ? { location: c.cal_location } : {}) })}`;

const CalButtons = ({ c }) => (
  <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`conf-cal-${c.id}`}>
    <CalendarPlus className="w-3.5 h-3.5 text-stone-400" />
    <a href={googleUrl(c)} target="_blank" rel="noreferrer" data-testid={`conf-cal-google-${c.id}`} className="px-2.5 py-1 rounded-full border border-[#E6DEC3] bg-[#FDFBF7] text-[#2C4A3E] hover:bg-[#F1EADB]">Google Calendar</a>
    <a href={`${API}/conferences/${c.id}/ics`} data-testid={`conf-cal-ics-${c.id}`} className="px-2.5 py-1 rounded-full border border-[#E6DEC3] bg-[#FDFBF7] text-[#2C4A3E] hover:bg-[#F1EADB]">Apple / Outlook</a>
  </div>
);

const LinkBox = ({ c, reload }) => {
  const [v, setV] = useState("");
  const save = async () => { try { await http.put(`/conferences/${c.id}/link`, { link: v.trim() }); toast.success("Link added"); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="flex gap-2 mt-2"><input value={v} onChange={(e) => setV(e.target.value)} placeholder="Paste video link (https://...)" data-testid={`conf-link-input-${c.id}`}
      className="flex-1 min-w-0 px-3 py-1.5 rounded-full bg-white border border-[#E6DEC3] text-xs" />
      <button onClick={save} data-testid={`conf-link-save-${c.id}`} className="px-3 rounded-full bg-[#2C4A3E] text-white text-xs">Save</button></div>
  );
};

const Item = ({ c, teacher, reload, onMove }) => {
  const Icon = ICON[c.mode];
  const cancel = async () => {
    if (!window.confirm("Cancel this check-in?")) return;
    try { await http.post(`/conferences/${c.id}/cancel`); toast.success("Check-in cancelled"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E6DEC3] p-3 text-xs" data-testid={`conf-${c.id}`}>
      <p className="flex items-center gap-2 text-sm text-[#2C4A3E]"><Icon className="w-4 h-4 text-[#C87D53]" /><b data-testid={`conf-when-${c.id}`}>{c.when}</b> · {c.mode_label} with {c.with}</p>
      {c.about?.length > 0 && <p className="text-stone-500 mt-1">About: {c.about.join(" & ")}</p>}
      {c.note && <p className="text-stone-600 mt-1">"{c.note}"</p>}
      {c.mode === "phone" && c.phone && <p className="text-stone-600 mt-1" data-testid={`conf-phone-${c.id}`}>{teacher ? "Call" : "Teacher will call"}: {c.phone}</p>}
      {c.mode === "in_person" && c.location && <p className="text-stone-600 mt-1">Where: {c.location}</p>}
      {c.mode === "video" && (c.link ? <a href={c.link} target="_blank" rel="noreferrer" className="block mt-1 underline text-[#2C4A3E] truncate" data-testid={`conf-link-${c.id}`}>Join video call</a>
        : teacher ? <LinkBox c={c} reload={reload} /> : <p className="text-stone-500 mt-1">The teacher will add the video link soon.</p>)}
      <CalButtons c={c} />
      <div className="flex gap-3 mt-2">
        <button onClick={() => onMove(c)} data-testid={`conf-reschedule-${c.id}`} className="underline text-[#2C4A3E]">Reschedule</button>
        <button onClick={cancel} data-testid={`conf-cancel-${c.id}`} className="underline text-[#8A3B2E]">Cancel</button>
      </div>
    </div>
  );
};

const useConfs = (withId) => {
  const [list, setList] = useState([]);
  const reload = useCallback(() => http.get("/conferences", { params: withId ? { with_id: withId } : {} }).then((r) => setList(r.data.conferences)).catch(() => {}), [withId]);
  useEffect(() => { reload(); }, [reload]);
  return [list, reload];
};

export const UpcomingList = () => {
  const { profile } = useProfile();
  const [list, reload] = useConfs(null);
  const [move, setMove] = useState(null);
  if (!list.length) return <p className="text-xs text-stone-500" data-testid="conf-none">No check-ins booked yet.</p>;
  return (
    <div className="space-y-2" data-testid="conf-upcoming">
      {list.map((c) => <Item key={c.id} c={c} teacher={profile.role === "teacher"} reload={reload} onMove={setMove} />)}
      {move && <BookCheckin open={!!move} setOpen={(v) => !v && setMove(null)} teacherId={move.teacher_id} reschedule={move} onDone={reload} />}
    </div>
  );
};

const MODE_TXT = { phone: "Phone call", in_person: "In person", video: "Video call" };

const NextBadge = ({ next, onPick }) => (next ? (
  <button onClick={() => onPick(next.start)} data-testid="next-open-badge" className="w-full text-left flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#FBF3E2] border border-[#F2D49B] text-[#7A4A12] text-xs hover:bg-[#F7EACD] transition-colors">
    <CalendarCheck className="w-4 h-4 shrink-0 text-[#C87D53]" /><span className="flex-1">Next open check-in: <b data-testid="next-open-time">{slotLabel(next.start)}</b> · {MODE_TXT[next.mode]}</span>
    <span className="px-3 py-1 rounded-full bg-[#C87D53] text-white">Book</span>
  </button>
) : <p className="px-4 py-2.5 rounded-2xl bg-[#F1EADB] text-stone-600 text-xs" data-testid="next-open-none">No open check-in times. Send a message to ask.</p>);

export const CheckinStrip = ({ other, tick, next, showNext }) => {
  const { profile } = useProfile();
  const teacher = profile.role === "teacher";
  const [list, reload] = useConfs(other.id);
  const [book, setBook] = useState(false);
  const [move, setMove] = useState(null);
  const [preset, setPreset] = useState(null);
  useEffect(() => { reload(); }, [tick, reload]);
  const teacherId = teacher ? profile.id : other.id;
  return (
    <div className="mx-5 mt-3 mb-1 space-y-2 shrink-0" data-testid="checkin-strip">
      {list.length > 0 && <div className="space-y-2 max-h-44 overflow-y-auto">{list.map((c) => <Item key={c.id} c={c} teacher={teacher} reload={reload} onMove={setMove} />)}</div>}
      {showNext && <NextBadge next={next} onPick={(st) => { setPreset(st); setBook(true); }} />}
      {!teacher && <button onClick={() => { setPreset(null); setBook(true); }} data-testid="book-checkin-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C87D53] text-white text-xs"><CalendarCheck className="w-4 h-4" />Book a check-in</button>}
      <BookCheckin open={book || !!move} setOpen={(v) => { if (!v) { setBook(false); setMove(null); setPreset(null); } }} teacherId={teacherId} reschedule={move} preset={move ? null : preset} onDone={reload} />
    </div>
  );
};
